import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { NAV_LINKS } from "../../lib/constants";

const renderLink = (l, className, onClick, testidPrefix, opts = {}) => {
  const testid = `${testidPrefix}-${l.label.toLowerCase().replace(/\s+/g, "-")}`;
  const useImage = l.image && !opts.forceText;
  const content = useImage ? (
    <img
      src={l.image}
      alt={l.label}
      className="h-7 md:h-8 w-auto object-contain transition-transform duration-300 group-hover:scale-[1.05]"
      draggable="false"
    />
  ) : (
    <>
      {l.label}
      {l.badge && (
        <span className="ml-1.5 inline-flex items-center rounded-full bg-[#3DC0AF]/15 border border-[#3DC0AF]/40 text-[9px] tracking-[0.2em] px-1.5 py-0.5 text-[#3DC0AF] font-mono">
          {l.badge}
        </span>
      )}
    </>
  );
  // Accent variant: same font/size as other nav links, but teal.
  const accentClass = l.accent && !useImage
    ? className.replace("text-zinc-300 hover:text-white", "text-[#3DC0AF] hover:text-[#00BA8B]")
              .replace("text-zinc-200", "text-[#3DC0AF]")
    : className;
  const mergedClass = useImage ? `${accentClass} group` : accentClass;
  if (l.route) {
    return (
      <Link
        key={l.href}
        to={l.href}
        data-testid={testid}
        onClick={onClick}
        aria-label={l.label}
        className={mergedClass}
      >
        {content}
      </Link>
    );
  }
  return (
    <a
      key={l.href}
      href={l.href}
      data-testid={testid}
      onClick={onClick}
      aria-label={l.label}
      className={mergedClass}
    >
      {content}
    </a>
  );
};

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid="site-navbar"
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.2, 0.8, 0.2, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-black/70 border-b border-white/10"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <Link
          to="/"
          data-testid="navbar-logo"
          className="flex items-center gap-2 group"
        >
          <img
            src="https://customer-assets.emergentagent.com/job_tbirdgeo-preview/artifacts/3gk1w13g_FullLogo_Transparent_NoBuffer%20%2812%29.png"
            alt="Thunderbird Geospatial Inc."
            className="h-12 md:h-14 w-auto object-contain transition-transform duration-500 group-hover:scale-[1.03]"
            draggable="false"
          />
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {NAV_LINKS.map((l) =>
            renderLink(
              l,
              "inline-flex items-center text-sm text-zinc-300 hover:text-white transition-colors tracking-wide",
              undefined,
              "nav-link"
            )
          )}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a
            href="/#quote"
            data-testid="navbar-cta"
            className="relative inline-flex items-center gap-2 rounded-full bg-[#3DC0AF] hover:bg-[#00BA8B] text-white text-sm font-medium px-5 py-2.5 transition-colors"
          >
            Request a Quote
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-white" />
          </a>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((v) => !v)}
          className="md:hidden inline-flex items-center justify-center h-10 w-10 rounded-md border border-white/10 text-white"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden border-t border-white/10 bg-black/90 backdrop-blur-xl"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {NAV_LINKS.map((l) =>
                renderLink(
                  l,
                  "flex items-center w-full text-zinc-200 text-base py-2",
                  () => setOpen(false),
                  "mobile-nav-link",
                  { forceText: true }
                )
              )}
              <a
                href="/#quote"
                onClick={() => setOpen(false)}
                data-testid="mobile-nav-cta"
                className="mt-2 inline-flex justify-center rounded-full bg-[#3DC0AF] text-white font-medium px-5 py-3"
              >
                Request a Quote
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
